/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import localStorage from '@ibm-watson/shared/modules/ia/util/localStorage';
import { SELECT_EMBED_TAB } from '@ibm-watson/shared/modules/ia/util/task-list/constants';
import { useChannelForm } from '../../hooks/useChannelForm';
import { WEBCHAT_CHANNEL_TYPE } from '../../util/channelTypes';
import { TabbedHeader } from '../common/tabbed-header/TabbedHeader';
import { Embed } from './embed-tab/Embed';
import { HomeScreen } from './home-screen-tab/HomeScreen';
import { Launcher } from './launcher-tab/Launcher';
import { LiveAgent } from './live-agent-tab/LiveAgent';
import { WebChat as messages } from './messages';
import { Resources } from './resources-tab/Resources';
import { settingsSchema } from './schema';
import { Security } from './security-tab/Security';
import { Style } from './style-tab/Style';
import { Suggestions } from './suggestions-tab/Suggestions';
import { WebChatSplashPage } from './WebChatSplashPage';

import './WebChat.scss';

interface WebChatProps {
  /**
   *
   */
  assistantId: string;
  /**
   *
   */
  environmentId?: string;
  /**
   *
   */
  integrationId: string;
  /**
   *
   */
  isNewClassicIntegration?: boolean;
  /**
   *
   */
  defaultTab?: number;
  /**
   * The callback function to fire when the user clicks a button/link to navigate to a page.
   */
  onClose?: () => void;
}
export const WebChat = ({
  assistantId,
  environmentId,
  integrationId,
  isNewClassicIntegration,
  onClose,
  defaultTab = 0
}: WebChatProps) => {
  const { formatMessage: t } = useIntl();
  const {
    loading,
    initialName,
    updateIntegrationName,
    formMethods,
    isReadOnly
  } = useChannelForm(
    WEBCHAT_CHANNEL_TYPE,
    assistantId,
    environmentId,
    integrationId,
    settingsSchema
  );

  const selectEmbedTab = localStorage.getContext()
    ? localStorage.get(SELECT_EMBED_TAB)
    : false;

  const [openingTab, setOpeningTab] = useState(defaultTab);

  // Note that the assistantId and environmentId properties will be
  // injected into each of these tab components via TabbedHeader.
  const tabs = [
    <Style
      key="tab-1"
      tabname={t(messages.styleTabLabel)}
      integrationId={integrationId}
      onClose={onClose}
    />,
    <Launcher
      key="tab-2"
      tabname={t(messages.launcherTabLabel)}
      environmentId={environmentId}
      integrationId={integrationId}
    />,
    <HomeScreen
      key="tab-3"
      tabname={t(messages.homeScreenTabLabel)}
      integrationId={integrationId}
    />,
    <LiveAgent key="tab-4" tabname={t(messages.liveAgentTabLabel)} />,
    <Suggestions
      key="tab-5"
      tabname={t(messages.suggestionsTabLabel)}
      integrationId={integrationId}
    />,
    <Security
      key="tab-6"
      assistantId={assistantId}
      environmentId={environmentId}
      integrationId={integrationId}
      tabname={t(messages.securityTabLabel)}
    />,
    <Embed
      key="tab-7"
      integrationId={integrationId}
      tabname={t(messages.embedTabLabel)}
    />,
    <Resources key="tab-8" tabname={t(messages.resourcesTabLabel)} />
  ];

  useEffect(() => {
    // If web chat is being opened from Preview button that says view embed details,
    // Embed tab should be shown upon opening integrations
    if (selectEmbedTab) {
      setOpeningTab(6);
    }
    localStorage.remove(SELECT_EMBED_TAB);
  }, [selectEmbedTab]);

  return (
    <FormProvider {...formMethods}>
      <TabbedHeader
        assistantId={assistantId}
        environmentId={environmentId}
        channelType={WEBCHAT_CHANNEL_TYPE}
        integrationName={initialName}
        updateIntegrationName={updateIntegrationName}
        useSplashPage={isNewClassicIntegration}
        loading={loading}
        defaultTab={openingTab}
        SplashPage={<WebChatSplashPage />}
        Tabs={tabs}
        readOnly={isReadOnly}
      />
    </FormProvider>
  );
};

export default WebChat;
